import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bar, Pie } from 'react-chartjs-2';
import { exportToExcel, exportToWord, printFiltered } from '../../utils/exportUtils';
import '../../styles/admin.css';

export default function ModuleAnalyticsPage() {
  const [tenants, setTenants] = useState([]);
  const [filters, setFilters] = useState({
    module: '',
    status: '',
    month: ''
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const res = await axios.get('/api/tenants');
      setTenants(res.data);
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const filteredTenants = tenants.filter((t) => {
    const matchesModule = filters.module
      ? t.modules.some((m) => m.name.toLowerCase() === filters.module.toLowerCase())
      : true;
    const matchesStatus = filters.status ? t.status === filters.status : true;
    const matchesMonth = filters.month ? t.unpaidMonth === filters.month : true;
    return matchesModule && matchesStatus && matchesMonth;
  });

  // 📊 Chart Data
  const moduleCounts = ['ATS', 'HRIS', 'PMS'].map((name) => ({
    name,
    count: filteredTenants.filter((t) => t.modules.some((m) => m.name === name)).length
  }));

  const activationData = {
    active: filteredTenants.filter((t) => t.modules.some((m) => m.status === 'enabled')).length,
    inactive: filteredTenants.filter((t) => t.modules.every((m) => m.status !== 'enabled')).length
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">📊 Module Analytics Dashboard</h2>

      {/* 🔍 Filters */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <select
          name="module"
          value={filters.module}
          onChange={handleFilterChange}
          className="border px-2 py-1 rounded text-sm"
        >
          <option value="">All Modules</option>
          <option value="ATS">ATS</option>
          <option value="HRIS">HRIS</option>
          <option value="PMS">PMS</option>
        </select>
        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          className="border px-2 py-1 rounded text-sm"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          name="month"
          value={filters.month}
          onChange={handleFilterChange}
          className="border px-2 py-1 rounded text-sm"
        >
          <option value="">All Months</option>
          {[
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
          ].map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        {/* 📤 Export Buttons */}
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => exportToExcel(filteredTenants)}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
          >
            Export Excel
          </button>
          <button
            onClick={() => exportToWord(filteredTenants)}
            className="bg-purple-600 text-white px-3 py-1 rounded text-sm"
          >
            Export Word
          </button>
          <button
            onClick={() => printFiltered(filteredTenants)}
            className="bg-green-600 text-white px-3 py-1 rounded text-sm"
          >
            Print
          </button>
        </div>
      </div>

      {/* 📊 Module Usage Bar Chart */}
      <div className="bg-white rounded shadow p-4 mb-6">
        <h3 className="text-lg font-semibold mb-2">Module Usage</h3>
        <Bar
          data={{
            labels: moduleCounts.map((m) => m.name),
            datasets: [
              {
                label: 'Tenant Count',
                data: moduleCounts.map((m) => m.count),
                backgroundColor: ['#4F46E5', '#10B981', '#F59E0B']
              }
            ]
          }}
          options={{ responsive: true, plugins: { legend: { display: false } } }}
        />
      </div>

      {/* 🟢 Activation Status Pie Chart */}
      <div className="bg-white rounded shadow p-4 mb-6">
        <h3 className="text-lg font-semibold mb-2">Module Activation Status</h3>
        <Pie
          data={{
            labels: ['Active', 'Inactive'],
            datasets: [
              {
                data: [activationData.active, activationData.inactive],
                backgroundColor: ['#22C55E', '#EF4444']
              }
            ]
          }}
          options={{ responsive: true }}
        />
      </div>

      {/* 💰 Unpaid Tenants Table */}
      <div className="bg-white rounded shadow p-4">
        <h3 className="text-lg font-semibold mb-2">Unpaid Tenants by Module</h3>
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-3 py-2 text-left">Tenant</th>
              <th className="px-3 py-2 text-left">Module</th>
              <th className="px-3 py-2 text-left">Unpaid Month</th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants
              .filter((t) => t.paymentStatus === 'unpaid')
              .flatMap((t) =>
                t.modules.map((m) => (
                  <tr key={`${t.tenantId}-${m.name}`} className="border-t">
                    <td className="px-3 py-2">{t.companyName}</td>
                    <td className="px-3 py-2">{m.name}</td>
                    <td className="px-3 py-2">{t.unpaidMonth}</td>
                  </tr>
                ))
              )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
